import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Debounced address / business autocomplete.
 * value: string (the display address)
 * onChange: (address, extras) => void  where extras = { lat, lng, place_id }
 */
export default function AddressAutocomplete({ value, onChange, placeholder, required, testId }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debRef = useRef(null);
  const suppress = useRef(false);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const runSearch = async (text) => {
    if (!text || text.length < 3) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/geo/autocomplete", { params: { q: text } });
      setItems(data.results || []);
      setOpen(true);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  const onType = (e) => {
    const v = e.target.value;
    setQ(v);
    onChange(v, {});
    if (suppress.current) { suppress.current = false; return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => runSearch(v), 300);
  };

  const pick = async (it) => {
    suppress.current = true;
    setQ(it.label);
    setOpen(false);
    setItems([]);
    let lat = it.lat, lng = it.lng;
    // If lat/lng missing (Google path), fetch details
    if ((lat == null || lng == null) && it.place_id) {
      try {
        const { data } = await api.get(`/geo/details/${it.place_id}`);
        lat = data.lat; lng = data.lng;
      } catch { /* ignore */ }
    }
    onChange(it.label, { lat, lng, place_id: it.place_id });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        required={required}
        value={q}
        onChange={onType}
        onFocus={() => q.length >= 3 && items.length && setOpen(true)}
        placeholder={placeholder || "Type a business or address..."}
        className="h-11 bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm placeholder:text-slate-400"
        autoComplete="off"
        data-testid={testId}
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green animate-spin" />}
      {open && items.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 border-2 border-brand-green bg-white max-h-72 overflow-y-auto z-[10000] shadow-lg" data-testid={`${testId}-dropdown`}>
          {items.map((it, i) => (
            <button
              key={it.place_id || i}
              type="button"
              onClick={() => pick(it)}
              className="w-full text-left px-3 py-2 hover:bg-brand-green hover:text-black font-mono text-xs border-b border-border last:border-0 flex gap-2"
              data-testid={`${testId}-option-${i}`}
            >
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-bold truncate">{it.main}</div>
                <div className="text-[10px] opacity-70 truncate">{it.secondary}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
